import { useCallback, useEffect, useState } from 'react';
import { log } from '@/lib/logger';

const STORAGE_KEY = 'compare_property_ids';
const MAX_ITEMS = 3;

function readIds() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_ITEMS) : [];
  } catch (e) {
    log.debug('useCompare: readIds failed', e);
    return [];
  }
}

function writeIds(ids) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch (e) {
    // Safari private mode / storage-quota failures — non-fatal.
    log.debug('useCompare: writeIds failed', e);
  }
  // Notify same-tab listeners
  window.dispatchEvent(new CustomEvent('compare:update', { detail: ids }));
}

/**
 * Client-side compare tray. Stores up to MAX_ITEMS property IDs in sessionStorage.
 * No sensitive data is persisted (property IDs are already public).
 */
export function useCompare() {
  const [ids, setIds] = useState(readIds);

  useEffect(() => {
    const onUpdate = (e) => setIds(e.detail || readIds());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setIds(readIds()); };
    window.addEventListener('compare:update', onUpdate);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('compare:update', onUpdate);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const isFull = ids.length >= MAX_ITEMS;
  const has = useCallback((id) => ids.includes(id), [ids]);

  const add = useCallback((id) => {
    const current = readIds();
    if (current.includes(id)) return { ok: true, alreadyIn: true };
    if (current.length >= MAX_ITEMS) return { ok: false, full: true };
    writeIds([...current, id]);
    return { ok: true };
  }, []);

  const remove = useCallback((id) => {
    writeIds(readIds().filter((x) => x !== id));
  }, []);

  const clear = useCallback(() => writeIds([]), []);

  const toggle = useCallback((id) => (has(id) ? (remove(id), { removed: true }) : add(id)), [has, add, remove]);

  return { ids, count: ids.length, max: MAX_ITEMS, isFull, has, add, remove, clear, toggle };
}
